// src/pages/AdminOrders.js
import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../AuthContext";
import { db } from "../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";

const ADMIN_UID = "MDSy4dIHEnOi3JMescY0PztvkK13";

function formatDate(ts) {
  if (!ts) return "";
  let d;
  if (ts.toDate) {
    d = ts.toDate();
  } else {
    d = new Date(ts);
  }
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

export default function AdminOrders() {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (!currentUser || currentUser.uid !== ADMIN_UID) {
      setOrders([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrMsg("");

    const q = query(
      collection(db, "orders"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = [];
        snap.forEach((docSnap) => {
          list.push({ id: docSnap.id, ...docSnap.data() });
        });
        setOrders(list);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to load orders:", err);
        setErrMsg("Failed to load orders.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [currentUser]);

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Delete this order permanently?")) return;
    try {
      await deleteDoc(doc(db, "orders", orderId));
    } catch (err) {
      console.error("Failed to delete order:", err);
      alert("Failed to delete order: " + err.message);
    }
  };

  const handleMarkCompleted = async (orderId, currentStatus) => {
    try {
      await updateDoc(doc(db, "orders", orderId), {
        status: "completed",
      });
    } catch (err) {
      console.error("Failed to update status:", err);
      alert("Failed to mark as completed: " + err.message);
    }
  };

  const isAdmin = currentUser && currentUser.uid === ADMIN_UID;

  return (
    <div className="flex flex-col min-h-screen text-white bg-gradient-to-b from-gray-900 via-purple-900 to-black">
      <Navbar />

      <main className="flex-grow relative z-10 flex flex-col items-center pt-24 px-6">
        <div className="w-full max-w-6xl bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 shadow-lg">
          <h1 className="text-2xl font-bold text-pink-400 mb-4">
            Admin – Orders
          </h1>

          {!isAdmin && (
            <p className="text-red-300">
              You are not authorized to view this page.
            </p>
          )}

          {isAdmin && (
            <>
              {loading && <p className="text-white/70">Loading orders...</p>}
              {errMsg && (
                <p className="text-red-300 mb-3">
                  {errMsg}
                </p>
              )}

              {!loading && orders.length === 0 && (
                <p className="text-white/60">No orders found yet.</p>
              )}

              {!loading && orders.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead>
                      <tr className="bg-black/40 text-white/80">
                        <th className="px-3 py-2 border-b border-white/10">
                          Order ID
                        </th>
                        <th className="px-3 py-2 border-b border-white/10">
                          Created
                        </th>
                        <th className="px-3 py-2 border-b border-white/10">
                          Pickup
                        </th>
                        <th className="px-3 py-2 border-b border-white/10">
                          Payment
                        </th>
                        <th className="px-3 py-2 border-b border-white/10">
                          Amounts
                        </th>
                        <th className="px-3 py-2 border-b border-white/10">
                          Items
                        </th>
                        <th className="px-3 py-2 border-b border-white/10">
                          Status
                        </th>
                        <th className="px-3 py-2 border-b border-white/10">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o) => {
                        const subtotal = o.subtotal ?? 0;
                        const deposit = o.depositAmount ?? 0;
                        const remaining = o.remainingAmount ?? 0;
                        const paymentMode = o.paymentMode || "deposit";

                        return (
                          <tr
                            key={o.id}
                            className="border-b border-white/10 hover:bg-white/5"
                          >
                            <td className="px-3 py-2 align-top break-all">
                              <div className="font-mono text-xs">
                                {o.id}
                              </div>
                              {o.userId && (
                                <div className="text-[11px] text-white/60 mt-1">
                                  userId: {o.userId}
                                </div>
                              )}
                            </td>

                            <td className="px-3 py-2 align-top">
                              <div>{formatDate(o.createdAt)}</div>
                            </td>

                            <td className="px-3 py-2 align-top">
                              <div>{o.pickupDate || "-"}</div>
                              <div className="text-xs text-white/60">
                                {o.pickupTime || ""}
                              </div>
                            </td>

                            <td className="px-3 py-2 align-top">
                              <div className="text-xs">
                                Mode:{" "}
                                <span className="font-semibold">
                                  {paymentMode === "full"
                                    ? "Full now"
                                    : "Deposit"}
                                </span>
                              </div>
                              {o.stripePaymentIntentId && (
                                <div className="text-[11px] text-white/60 mt-1 break-all">
                                  PI: {o.stripePaymentIntentId}
                                </div>
                              )}
                            </td>

                            <td className="px-3 py-2 align-top">
                              <div className="text-xs">
                                Subtotal:{" "}
                                <span className="font-semibold">
                                  ${subtotal.toFixed(2)}
                                </span>
                              </div>
                              <div className="text-xs">
                                Paid now:{" "}
                                <span className="font-semibold">
                                  ${deposit.toFixed(2)}
                                </span>
                              </div>
                              {remaining > 0 && (
                                <div className="text-xs">
                                  Remaining:{" "}
                                  <span className="font-semibold">
                                    ${remaining.toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </td>

                            <td className="px-3 py-2 align-top">
                              {Array.isArray(o.items) && o.items.length > 0 ? (
                                <ul className="list-disc list-inside text-xs space-y-1">
                                  {o.items.map((it, idx) => (
                                    <li key={idx}>
                                      {it.name} – ${Number(it.price || 0).toFixed(2)}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-xs text-white/60">
                                  (no items)
                                </span>
                              )}
                            </td>

                            <td className="px-3 py-2 align-top">
                              <span
                                className={
                                  "px-2 py-1 rounded-full text-xs " +
                                  (o.status === "completed"
                                    ? "bg-green-500/20 text-green-300"
                                    : o.status === "deposit-paid"
                                    ? "bg-yellow-500/20 text-yellow-300"
                                    : "bg-white/10 text-white/80")
                                }
                              >
                                {o.status || "unknown"}
                              </span>
                            </td>

                            <td className="px-3 py-2 align-top">
                              <div className="flex flex-col gap-2">
                                <button
                                  onClick={() =>
                                    handleMarkCompleted(o.id, o.status)
                                  }
                                  disabled={o.status === "completed"}
                                  className={
                                    "text-xs px-3 py-1 rounded-md " +
                                    (o.status === "completed"
                                      ? "bg-gray-500/40 text-gray-200 cursor-not-allowed"
                                      : "bg-green-500/80 text-white hover:bg-green-600")
                                  }
                                >
                                  {o.status === "completed"
                                    ? "Completed"
                                    : "Mark Completed"}
                                </button>

                                <button
                                  onClick={() => handleDeleteOrder(o.id)}
                                  className="text-xs px-3 py-1 rounded-md bg-red-600/80 text-white hover:bg-red-700"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
